import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';

import { ENV_CONFIG } from '@ps/core-config';
import { withLoading } from '@ps/core-loading';
import type { SearchPSIDsParams, UploadPsidFileResult } from '@ps/survey-utils';

import { UploadPsidFileResponseDto } from '../dtos/upload-psid-file.dto';
import { UploadPsidFileMapper } from '../mapper/upload-psid-file.mapper';
import {
  PsidCountsRequestDto,
  PsidCountsResponseDto,
  PsidListRequestDto,
  PsidListResponseDto,
} from './psid.dto';

@Injectable({ providedIn: 'root' })
export class PsidApi {
  private readonly _env = inject(ENV_CONFIG);
  private readonly _http = inject(HttpClient);

  private readonly _baseUrl = `${this._env.pureSpectrum.url}`;
  private readonly _uploadPsidFileMapper = new UploadPsidFileMapper();

  public getAll(
    fromDate: string,
    toDate: string,
    surveyIds: number[],
  ): Observable<SearchPSIDsParams> {
    const body: PsidListRequestDto = { fromDate, toDate, surveyIds };
    return this._http
      .post<PsidListResponseDto>(
        `${this._baseUrl}/buyers/v2/surveys/psids`,
        body,
        { context: withLoading('drawer') },
      )
      .pipe(
        map((response) => ({
          surveyIds: response.survey_ids,
          refs: response.ref_list,
        })),
      );
  }

  public getCounts(
    isExclusion: boolean,
    surveyIds: number[],
    groupingStatuses: number[],
  ): Observable<number> {
    const body: PsidCountsRequestDto = {
      include_quotas_count: false,
      is_exclusion: isExclusion,
      survey_grouping: surveyIds,
      grouping_statuses: groupingStatuses,
    };

    return this._http
      .post<PsidCountsResponseDto>(
        `${this._baseUrl}/buyers/v2/surveys/PSID/counts`,
        body,
        { context: withLoading('drawer') },
      )
      .pipe(map((response) => response.total_count));
  }

  public uploadPsidFile(
    surveyId: number,
    file: File,
  ): Observable<UploadPsidFileResult> {
    const formData = new FormData();
    formData.append('psid_ref_file', file);
    formData.append('surveyId', String(surveyId));
    return this._http
      .post<UploadPsidFileResponseDto>(
        `${this._baseUrl}/inclexcl/PSIDFile`,
        formData,
      )
      .pipe(map((dto) => this._uploadPsidFileMapper.fromDto(dto)));
  }

  public clearUploadedPsids(surveyId: number): Observable<void> {
    return this._http.delete<void>(
      `${this._baseUrl}/inclexcl/clearUploadedPSIDs/${surveyId}`,
    );
  }
}
